import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import type { JSONContent } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { useEditor } from '@tiptap/react';
import cn from 'classnames';

import { useVideoUpload, type VideoUploadControls } from '@/video/useVideoUpload';

import { COMMUNITY_VIDEO_UPLOAD_LIMITS, tr } from './_components/constants';
import { ComposerBody } from './_components/layout/ComposerBody';
import { ComposerFooter } from './_components/layout/ComposerFooter';
import { ComposerFrame } from './_components/layout/ComposerFrame';
import { ScheduledTimeChip } from './_components/layout/ComposerSchedule';
import { openScheduling } from './_components/layout/openScheduling';
import { Image as ImageNode } from './_components/nodes/Image/Image';
import { isUploadingImageSrc } from './_components/nodes/Image/isUploadingImageSrc';
import { LinkCard as LinkCardNode } from './_components/nodes/LinkCard/LinkCard';
import { LinkEmbed } from './_components/nodes/LinkEmbed/LinkEmbed';
import { LinkPrompt, linkPromptKey } from './_components/nodes/LinkPrompt/LinkPrompt';
import { Video as VideoNode } from './_components/nodes/Video/Video';
import { type ImagePasteControls, useImagePaste } from './_components/useImagePaste';
import { useVideoDrop } from './_components/useVideoDrop';
import { VideoDropOverlay } from './_components/VideoDropOverlay';
import { VideoUploadContext } from './_components/videoUploadContext';
import type { CommunityPostComposerCoreProps, CommunityPostComposerHandle } from './CommunityPostComposer.type';
import { createCommunityStarterKit } from './createCommunityStarterKit';
import { createEmojiExtension } from './createEmojiExtension';
import { createMentionExtension } from './createMentionExtension';
import { SubmitShortcut } from './SubmitShortcut';
import { TrailingNode } from './TrailingNode';

const emptyDoc: JSONContent = { type: 'doc', content: [{ type: 'paragraph' }] };

// A placeholder video node (no videoId yet) or a pasted image still on its preview URL
// is an in-progress upload, not content. Strip it from anything the host reads or
// persists; submission stays blocked until it resolves or is removed.
const isProvisionalMedia = (node: JSONContent): boolean =>
  (node.type === 'video' && !node.attrs?.videoId) || (node.type === 'image' && isUploadingImageSrc(node.attrs?.src));

const stripProvisionalMedia = (doc: JSONContent): JSONContent => {
  if (!doc.content) return doc;
  return {
    ...doc,
    content: doc.content.filter((child) => !isProvisionalMedia(child)).map(stripProvisionalMedia),
  };
};

const findProvisionalMedia = (editor: { state: { doc: import('@tiptap/pm/model').Node } }) => {
  const provisional = { video: false, image: false };
  editor.state.doc.descendants((node) => {
    if (node.type.name === 'video' && !node.attrs.videoId) provisional.video = true;
    if (node.type.name === 'image' && isUploadingImageSrc(node.attrs.src)) provisional.image = true;
  });
  return provisional;
};

export const CommunityPostComposerCore = forwardRef<CommunityPostComposerHandle, CommunityPostComposerCoreProps>(
  (
    {
      initialContent,
      placeholder,
      title,
      onTitleChange,
      showTitleInput = true,
      autoFocusTitle = true,
      titleMaxLength,
      editorMinHeight,
      isEditMode = false,
      quotedPost,
      onQuotedPostRemove,
      onSubmit,
      isSubmitting = false,
      isSubmitDisabled = false,
      scheduling,
      isHidden = false,
      onContentChange,
      onUploadImage,
      isMediaUploadDisabled,
      uploadAccept,
      uploadMaxBytes,
      videoUploadAdapter,
      videoUploadLimits = COMMUNITY_VIDEO_UPLOAD_LIMITS,
      videoUploadAccept,
      locale,
      subscribeToVideoStatus,
      onRetryVideoProcessing,
      resolveVideoPlayback,
      loadVideoThumbnails,
      selectVideoThumbnail,
      uploadVideoThumbnail,
      onSearchGifs,
      gifSearchLimit,
      gifSearchDebounceMs,
      onFetchLinkPreview,
      mentionSuggestions,
      attachableLists,
      onPostQueryChange,
      isLoadingPosts,
      attached,
      onAttachedChange,
      maxAttachedPosts = 1,
      transformImageUrl,
      error,
      translations,
      className,
    },
    ref,
  ) => {
    const [isEmpty, setIsEmpty] = useState(() => !initialContent);
    const [provisionalMedia, setProvisionalMedia] = useState(false);
    const [videoUnplayable, setVideoUnplayable] = useState(false);

    const keyboardSubmitRef = useRef<() => void>(() => {});
    const clockRef = useRef<HTMLButtonElement>(null);
    const chipRef = useRef<HTMLButtonElement>(null);
    const openedFromChipRef = useRef(false);
    const wasHiddenRef = useRef(isHidden);
    // The editor is created before the upload hook, so onUpdate reaches it through a ref.
    const videoUploadRef = useRef<VideoUploadControls | null>(null);
    const imagePasteRef = useRef<ImagePasteControls | null>(null);

    const onContentChangeRef = useRef(onContentChange);
    onContentChangeRef.current = onContentChange;

    const mentionSuggestionsRef = useRef(mentionSuggestions);
    mentionSuggestionsRef.current = mentionSuggestions;

    const hasSubmit = Boolean(onSubmit);
    // Primitives, so a host passing a new limits object with the same values keeps the editor.
    const { max_bytes: maxVideoBytes, max_duration_in_ms: maxVideoDurationInMs } = videoUploadLimits;

    const extensions = useMemo(
      () => [
        createCommunityStarterKit(),
        Placeholder.configure({ placeholder: placeholder ?? 'Write something…' }),
        Underline,
        Link.configure({ openOnClick: false, linkOnPaste: true }),
        ImageNode.configure({
          inline: false,
          allowBase64: false,
          HTMLAttributes: { class: 'community-editor-image' },
          translations,
        }),
        LinkEmbed,
        VideoNode.configure({
          subscribeToStatus: subscribeToVideoStatus,
          onRetry: onRetryVideoProcessing,
          resolvePlayback: resolveVideoPlayback,
          loadThumbnails: loadVideoThumbnails,
          selectThumbnail: selectVideoThumbnail,
          uploadThumbnail: uploadVideoThumbnail,
          onUnplayableChange: setVideoUnplayable,
          uploadLimits: { max_bytes: maxVideoBytes, max_duration_in_ms: maxVideoDurationInMs },
          locale,
          translations,
        }),
        LinkPrompt,
        TrailingNode,
        createEmojiExtension(),
        createMentionExtension({
          getSuggestions: () => mentionSuggestionsRef.current,
          fallbackLabel: tr(translations, 'community.join.former_member'),
          listLabel: tr(translations, 'community.mentions.list_label'),
        }),
        // Must come after StarterKit so its Mod-Enter binding wins over HardBreak's.
        // Only registered when the host wants submit behavior at all.
        ...(hasSubmit
          ? [
              SubmitShortcut.configure({
                onSubmit: () => keyboardSubmitRef.current(),
                shouldYieldEnter: (e) => Boolean(linkPromptKey.getState(e.state)),
              }),
            ]
          : []),
        LinkCardNode.configure({ onFetchLinkPreview, translations }),
      ],
      [
        placeholder,
        onFetchLinkPreview,
        translations,
        hasSubmit,
        subscribeToVideoStatus,
        onRetryVideoProcessing,
        resolveVideoPlayback,
        loadVideoThumbnails,
        selectVideoThumbnail,
        uploadVideoThumbnail,
        maxVideoBytes,
        maxVideoDurationInMs,
        locale,
      ],
    );

    const editor = useEditor({
      extensions,
      content: initialContent ?? emptyDoc,
      editorProps: {
        attributes: { class: 'flex-1' },
        handlePaste: (_view, event) => imagePasteRef.current?.handlePaste(event) ?? false,
      },
      onUpdate: ({ editor: e, transaction }) => {
        setIsEmpty(e.isEmpty);
        const provisional = findProvisionalMedia(e);
        setProvisionalMedia(provisional.video || provisional.image);
        // The placeholder and its upload live and die together. Deleting the
        // placeholder any other way than its cancel button (Backspace, cut) stops the
        // upload; a placeholder with no upload behind it (undo after such a delete) goes.
        const upload = videoUploadRef.current;
        if (upload && provisional.video !== upload.isBacking()) {
          if (provisional.video) e.commands.removeProvisionalVideo();
          else upload.cancel();
        }
        if (provisional.image) imagePasteRef.current?.syncUploads();
        // Upload bookkeeping stays out of undo history. Any other change is the member editing.
        if (transaction.getMeta('addToHistory') !== false) imagePasteRef.current?.clearError();
        onContentChangeRef.current?.(e.isEmpty ? null : stripProvisionalMedia(e.getJSON()));
      },
    });

    useEffect(() => {
      if (!editor) return;
      const next = initialContent ?? emptyDoc;
      editor.commands.setContent(next);
      setIsEmpty(editor.isEmpty);
    }, [editor, initialContent]);

    useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          if (!editor) return;
          editor.commands.setContent(initialContent ?? emptyDoc);
          setIsEmpty(editor.isEmpty);
          imagePasteRef.current?.clearError();
        },
        getContent: () => (editor ? stripProvisionalMedia(editor.getJSON()) : emptyDoc),
        isEmpty: () => editor?.isEmpty ?? true,
      }),
      [editor, initialContent],
    );

    const [videoPanelOpen, setVideoPanelOpen] = useState(false);

    const videoUpload = useVideoUpload({
      adapter: videoUploadAdapter,
      // The bytes have landed and the backend confirmed an id: fill it into the
      // placeholder that has been sitting in the doc, keeping its position.
      onCompleted: (video) => {
        editor?.commands.setVideoId(video.id);
      },
      limits: videoUploadLimits,
    });
    videoUploadRef.current = videoUpload;

    const imagePaste = useImagePaste({
      editor,
      onUploadImage,
      disabled: isMediaUploadDisabled,
      accept: uploadAccept,
      maxBytes: uploadMaxBytes,
      translations,
    });
    imagePasteRef.current = imagePaste;

    // A picked or dropped file starts the upload and drops a placeholder at the drop
    // point (or the selection for the picker) so the author sees exactly where the
    // video will land. A file the upload rejects keeps its placeholder, which says why.
    // A busy upload rejects replacements, whether dropped or picked.
    const handleVideoFile = useCallback(
      (file: File, event?: { clientX: number; clientY: number }) => {
        if (videoUpload.isBusy) return;
        void videoUpload.startUpload(file);
        const pos =
          event && editor ? editor.view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos : undefined;
        editor?.chain().focus().insertProvisionalVideo(pos).run();
        setVideoPanelOpen(false);
      },
      [videoUpload, editor],
    );

    const videoDropEnabled = Boolean(videoUploadAdapter);
    const { isDragActive, dropHandlers } = useVideoDrop({
      disabled: videoUpload.isBusy,
      onFile: (file, dropEvent) => handleVideoFile(file, dropEvent),
    });
    const videoDrop = videoDropEnabled
      ? {
          handlers: dropHandlers,
          isActive: isDragActive,
          overlay: <VideoDropOverlay translations={translations} limits={videoUploadLimits} locale={locale} />,
        }
      : undefined;

    const handleSubmit = useCallback(() => {
      onSubmit?.(editor ? stripProvisionalMedia(editor.getJSON()) : emptyDoc);
    }, [editor, onSubmit]);

    const hasAttachedPost = (attached?.posts?.length ?? 0) > 0;
    // Block submission while an upload is mid-flight or its placeholder is still in
    // the doc (uploading, finishing, or errored awaiting retry/removal), or while the
    // video failed or was blocked.
    const submitBlocked =
      isSubmitDisabled ||
      videoUpload.isBusy ||
      provisionalMedia ||
      videoUnplayable ||
      (isEmpty && !quotedPost && !hasAttachedPost);

    const keyboardSubmit = () => {
      if (isSubmitting || submitBlocked) return;
      handleSubmit();
    };
    keyboardSubmitRef.current = keyboardSubmit;

    // The picker's Schedule follows Post. The composer stays mounted under the picker, so an upload that finishes there
    // still counts.
    const onCanSubmitChange = scheduling?.onCanSubmitChange;
    useEffect(() => {
      onCanSubmitChange?.(!submitBlocked);
    }, [onCanSubmitChange, submitBlocked]);

    useEffect(() => {
      if (wasHiddenRef.current && !isHidden) {
        ((openedFromChipRef.current && chipRef.current) || clockRef.current)?.focus();
        openedFromChipRef.current = false;
      }
      wasHiddenRef.current = isHidden;
    }, [isHidden]);

    return (
      <VideoUploadContext.Provider value={videoUploadAdapter ? videoUpload : null}>
        <ComposerFrame className={cn(className, { hidden: isHidden })}>
          <ComposerBody
            editor={editor}
            title={title}
            onTitleChange={onTitleChange}
            onKeyboardSubmit={keyboardSubmit}
            showTitleInput={showTitleInput}
            autoFocusTitle={autoFocusTitle}
            titleMaxLength={titleMaxLength}
            editorMinHeight={editorMinHeight}
            quotedPost={quotedPost}
            onQuotedPostRemove={onQuotedPostRemove}
            attached={attached}
            onAttachedChange={onAttachedChange}
            transformImageUrl={transformImageUrl}
            error={error ?? imagePaste.error}
            belowContent={
              scheduling?.scheduled && (
                <ScheduledTimeChip
                  buttonRef={chipRef}
                  scheduled={scheduling.scheduled}
                  onOpen={() => {
                    openedFromChipRef.current = true;
                    openScheduling(editor, scheduling.onOpen);
                  }}
                  disabled={isSubmitting}
                />
              )
            }
            translations={translations}
            videoDrop={videoDrop}
          />
          <ComposerFooter
            editor={editor}
            isEditMode={isEditMode}
            isSubmitting={isSubmitting}
            isSubmitDisabled={submitBlocked}
            onSubmit={hasSubmit ? handleSubmit : undefined}
            scheduling={scheduling}
            clockRef={clockRef}
            translations={translations}
            onUploadImage={onUploadImage}
            isMediaUploadDisabled={isMediaUploadDisabled}
            uploadAccept={uploadAccept}
            uploadMaxBytes={uploadMaxBytes}
            onSelectVideoFile={videoUploadAdapter ? handleVideoFile : undefined}
            videoUploadLimits={videoUploadLimits}
            videoUploadAccept={videoUploadAccept}
            locale={locale}
            videoPanelOpen={videoPanelOpen}
            onVideoPanelOpenChange={setVideoPanelOpen}
            onSearchGifs={onSearchGifs}
            gifSearchLimit={gifSearchLimit}
            gifSearchDebounceMs={gifSearchDebounceMs}
            attachableLists={attachableLists}
            onPostQueryChange={onPostQueryChange}
            isLoadingPosts={isLoadingPosts}
            attached={attached}
            onAttachedChange={onAttachedChange}
            maxAttachedPosts={maxAttachedPosts}
            transformImageUrl={transformImageUrl}
          />
        </ComposerFrame>
      </VideoUploadContext.Provider>
    );
  },
);

CommunityPostComposerCore.displayName = 'CommunityPostComposerCore';
